
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useVendors } from './hooks/useVendors';

// Define the type inline to avoid import issues
interface VendorInput {
  name: string;
  contactPerson: string;
  email?: string;
  phone?: string;
  address?: string;
  gstin?: string;
  category: 'Gold' | 'Diamond' | 'Silver' | 'Accessories';
  bank?: {
    account?: string;
    ifsc?: string;
  };
  documents?: string[];
}

export const VendorDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { byId, update, updateStatus } = useVendors();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<VendorInput | null>(null);

  const vendorQuery = byId(id!);
  const vendor = vendorQuery.data;

  const handleEdit = () => {
    setFormData(vendor);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!formData || !id) return;
    
    try {
      await update.mutateAsync({ id, data: formData });
      setIsEditing(false);
      setFormData(null);
    } catch (error) {
      console.error('Failed to update vendor:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(null);
  };

  const handleStatusChange = async (status: string) => {
    if (!id) return;
    
    try {
      await updateStatus.mutateAsync({ id, status });
    } catch (error) {
      console.error('Failed to update vendor status:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (vendorQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading vendor details...</div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-4">Vendor not found</div>
          <button
            onClick={() => navigate('/vendor/list')}
            className="text-[var(--color-primary)] hover:text-[var(--color-gold)] font-medium cursor-pointer"
          >
            Back to Vendor List
          </button>
        </div>
      </div>
    );
  }

  const currentData = isEditing ? formData : vendor;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start mb-8 space-y-4 md:space-y-0">
          <div>
            <h1 className="text-3xl font-semibold text-[var(--color-primary)] mb-2">
              {currentData?.name}
            </h1>
            <div className="flex flex-wrap items-center gap-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(vendor.status)}`}>
                {vendor.status}
              </span>
              <span className="text-gray-600">ID: {vendor.id}</span>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            {!isEditing ? (
              <>
                <button
                  onClick={handleEdit}
                  className="px-4 py-2 bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-gold)] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Edit
                </button>
                {vendor.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleStatusChange('approved')}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleStatusChange('suspended')}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Reject
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={update.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {update.isPending ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Basic Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData?.name || ''}
                    onChange={(e) => setFormData(prev => prev ? { ...prev, name: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-medium">{currentData?.name}</p>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData?.contactPerson || ''}
                    onChange={(e) => setFormData(prev => prev ? { ...prev, contactPerson: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-medium">{currentData?.contactPerson}</p>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                {isEditing ? (
                  <select
                    value={formData?.category || 'Gold'}
                    onChange={(e) => setFormData(prev => prev ? { ...prev, category: e.target.value as any } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent pr-8"
                  >
                    <option value="Gold">Gold</option>
                    <option value="Diamond">Diamond</option>
                    <option value="Silver">Silver</option>
                    <option value="Accessories">Accessories</option>
                  </select>
                ) : (
                  <p className="text-gray-900 font-medium">{currentData?.category}</p>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData?.gstin || ''}
                    onChange={(e) => setFormData(prev => prev ? { ...prev, gstin: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-medium">{currentData?.gstin || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Contact Information</h3>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                {isEditing ? (
                  <input
                    type="email"
                    value={formData?.email || ''}
                    onChange={(e) => setFormData(prev => prev ? { ...prev, email: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-medium">{currentData?.email || 'Not provided'}</p>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={formData?.phone || ''}
                    onChange={(e) => setFormData(prev => prev ? { ...prev, phone: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-medium">{currentData?.phone || 'Not provided'}</p>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                {isEditing ? (
                  <textarea
                    value={formData?.address || ''}
                    onChange={(e) => setFormData(prev => prev ? { ...prev, address: e.target.value } : null)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-medium">{currentData?.address || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bank Details */}
        {(currentData?.bank?.account || currentData?.bank?.ifsc || isEditing) && (
          <div className="mt-8 pt-8 border-t">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Bank Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData?.bank?.account || ''}
                    onChange={(e) => setFormData(prev => prev ? { 
                      ...prev, 
                      bank: { ...prev.bank, account: e.target.value }
                    } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-medium">{currentData?.bank?.account || 'Not provided'}</p>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData?.bank?.ifsc || ''}
                    onChange={(e) => setFormData(prev => prev ? { 
                      ...prev, 
                      bank: { ...prev.bank, ifsc: e.target.value }
                    } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-medium">{currentData?.bank?.ifsc || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="mt-8 pt-8 border-t">
          <button
            onClick={() => navigate('/vendor/list')}
            className="flex items-center text-[var(--color-primary)] hover:text-[var(--color-gold)] font-medium cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 mr-2 flex items-center justify-center"></i>
            Back to Vendor List
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorDetail;
